/**
 * BottomCard 底部卡片组件（独立导出）
 * 可以作为 MetricBarCard 的插槽使用，也可以独立使用
 */

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { formatMetricValue } from '../MetricBarCard/_utils';
import type { IBottomCardProps } from '../MetricBarCard/_type';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import { dynamicString } from '@home/utils/dynamicString';
import { Bubble } from '@home/components/TeaComponents';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';

/**
 * 底部卡片组件（可独立使用）
 */
const BottomCard: React.FC<IBottomCardProps> = (props) => {
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const {
    className,
    style,
    data,
    metricCardData = [],
    componentMetrics = [],
  } = props;

  // 从 componentMetrics 中获取完整配置
  const finalMetricConfig1 = useMemo(() => componentMetrics?.[0], [componentMetrics]);
  const finalMetricConfig2 = useMemo(() => componentMetrics?.[1], [componentMetrics]);

  // 从 metricCardData 中获取指标值
  const metricData1 = useMemo(() => {
    if (!finalMetricConfig1) return { value: '', dod: '' };
    return (
      metricCardData.find(d => d.metrics_name === finalMetricConfig1.metricKey) || {
        value: '',
        dod: '',
      }
    );
  }, [finalMetricConfig1, metricCardData]);

  const metricData2 = useMemo(() => {
    if (!finalMetricConfig2) return { value: '' };
    return metricCardData.find(d => d.metrics_name === finalMetricConfig2.metricKey) || { value: '' };
  }, [finalMetricConfig2, metricCardData]);

  // 主标题和涨跌来自 metric1
  const { title, rate, hasDod, subTitle, explain } = useMemo(() => {
    if (!finalMetricConfig1) {
      return { title: '', rate: '', hasDod: false, subTitle: '' };
    }
    return {
      title: getName(isEn, dynamicString(finalMetricConfig1.nameEn, data), dynamicString(finalMetricConfig1.nameCn, data)) || finalMetricConfig1.name || "",
      explain: getName(isEn, dynamicString(finalMetricConfig1.explainEn, data), dynamicString(finalMetricConfig1.explainCn, data)) || finalMetricConfig1.explain || "",
      rate: metricData1?.dod,
      hasDod: finalMetricConfig1?.hasDod,
      subTitle: finalMetricConfig1?.subTitle,
    };
  }, [isEn, finalMetricConfig1, metricData1, data]);

  // 主数值来自 metric1（与标题和涨跌保持一致）
  const { value, preUnit, endUnit } = useMemo(() => {
    if (!finalMetricConfig1) {
      return { value: '', preUnit: '', endUnit: '' };
    }
    return {
      value: metricData1?.value,
      preUnit: finalMetricConfig1?.preUnit,
      endUnit: finalMetricConfig1?.endUnit,
    };
  }, [finalMetricConfig1, metricData1]);

  const displayValue = useMemo(() => {
    if (value === undefined || value === null || value === '') return '-';
    return formatMetricValue(finalMetricConfig1, value, data);
  }, [value, finalMetricConfig1, data]);

  const rateValue = useMemo(() => {
    if (rate === undefined || rate === null || rate === '') return '';
    const raw = typeof rate === 'number' ? rate : parseFloat(String(rate).replace('%', ''));
    if (Number.isNaN(raw)) return '';

    // 对齐系统卡片：接口 dod 通常为比例（如 0.021），展示为整数百分比（如 2%）
    const percent = Math.abs(raw) <= 1 ? raw * 100 : raw;
    // 保留整数，保留正负号
    const fixed = Number(percent.toFixed(0));
    return Number.isNaN(fixed) ? '' : fixed;
  }, [rate]);

  // 底部数值也来自 metric2（如果需要单独显示）
  const { metric2ValueDisplay, metric2Explain } = useMemo(() => {
    if (!finalMetricConfig2) return { metric2ValueDisplay: '', metric2Explain: '' };
    const formatted = formatMetricValue(finalMetricConfig2, metricData2.value || '', data);
    if (!formatted) return { metric2ValueDisplay: '', metric2Explain: '' };
    const prefix = finalMetricConfig2.preUnit || '';
    const suffix = finalMetricConfig2.endUnit || '';
    const displayValue = formatted === '-' ? '-' : `${prefix}${formatted}${suffix}`;
    const explainText = getName(
      isEn,
      dynamicString(finalMetricConfig2.explainEn, data),
      dynamicString(finalMetricConfig2.explainCn, data)
    ) || finalMetricConfig2.explain || '';
    return { metric2ValueDisplay: displayValue, metric2Explain: explainText };
  }, [finalMetricConfig2, metricData2.value, data, isEn]);

  return (
    <div className={classNames('stat-card', 'dbd-bottom-bar-stat-card', className)} style={style}>
      <div className="dbd-stat-card-wrap">
        <Bubble content={explain}>
          <div className="dbd-stat-card-title">
            <span>{title}</span>
          </div>
        </Bubble>
      </div>
      {subTitle && <div className="dbd-stat-card-sub-title">{subTitle}</div>}
      <div className={`dbd-stat-card-content ${subTitle ? 'margin-t-0' : ''}`}>
        <div className="dbd-stat-card-value">
          <div>
            {displayValue === '-' ? '-' : `${preUnit}${displayValue}${endUnit}`}
          </div>
        </div>
        {hasDod && rateValue !== '' && (
          <div className="dbd-stat-card-rate">
            {/* TODO: 涨跌幅展示待实现 */}
          </div>
        )}
      </div>
      {metric2ValueDisplay && (
        <CommonBubble content={metric2Explain}>
          <div className="dbd-bottom-card-sub-value">
            {metric2ValueDisplay}
          </div>
        </CommonBubble>
      )}
    </div>
  );
};

export default BottomCard;
